import React, { useEffect, useRef, useState } from "react";
import { wsRequest, onBroadcast } from "./wsClient.js";
import { showToast } from "./toast.js";

// OPERATIONAL ISSUES, in the admin header.
//
// The same shape as the public notification bell in SiteHeader, and
// deliberately NOT the same list. That one is shown to every visitor and
// its action has no permission check at all; "Resend refused this send"
// is not something a buyer can act on.
//
// Two differences from the public bell, both on purpose:
//
//   - CLEARING IS SERVER-SIDE. The public bell clears per browser, since
//     dismissing a "competition won" notice is a personal act. An
//     operational issue is not: if one admin has dealt with it, the
//     others should not still be looking at it.
//
//   - THE COUNT IS EVERY ISSUE, not unread ones. There is no "seen"
//     state to keep — an issue is outstanding until somebody clears it.
export default function AdminIssuesBell({ token, canSee }) {
  const [issues, setIssues] = useState([]);
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const containerRef = useRef(null);

  const load = () => {
    if (!token || !canSee) return;
    wsRequest("admin.issues.list", {}, token)
      .then((list) => setIssues(Array.isArray(list) ? list : []))
      .catch(() => {});
  };

  useEffect(load, [token, canSee]);

  useEffect(
    () =>
      onBroadcast((message) => {
        if (message.type === "admin-issues-changed") load();
      }),
    [token, canSee]
  );

  // Close when the click lands anywhere else, same as the public bell.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (!canSee) return null;

  const clearAll = async () => {
    setClearing(true);
    try {
      const res = await wsRequest("admin.issues.clear", {}, token);
      showToast(`Cleared ${res.cleared} issue${res.cleared === 1 ? "" : "s"}.`, "success");
      setOpen(false);
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setClearing(false);
  };

  const KIND_LABEL = {
    email: "Email",
    replay: "Replay",
    demo: "Demo",
    payment: "Payment",
    competition: "Competition",
  };

  const fmt = (ms) => {
    try {
      return new Date(ms).toLocaleString();
    } catch {
      return "";
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={issues.length ? `${issues.length} issue(s)` : "No issues"}
        style={{
          background: "var(--bg-panel-alt)",
          border: "1px solid var(--border-color-light)",
          borderRadius: 6,
          color: "var(--text-primary)",
          padding: "8px 10px",
          cursor: "pointer",
          fontSize: 14,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        ⚠️
        {issues.length > 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              background: "var(--accent-red)",
              color: "#fff",
              borderRadius: 10,
              padding: "1px 6px",
            }}
          >
            {issues.length}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 6px)",
            width: 380,
            maxHeight: 420,
            overflowY: "auto",
            background: "var(--bg-panel)",
            border: "1px solid var(--border-color-light)",
            borderRadius: 8,
            padding: 12,
            zIndex: 60,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          }}
        >
          {issues.length === 0 ? (
            <p style={{ margin: 0, opacity: 0.7, fontSize: 13 }}>Nothing to report.</p>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <button
                  type="button"
                  disabled={clearing}
                  onClick={clearAll}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--accent-blue)",
                    cursor: "pointer",
                    fontSize: 13,
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  {clearing ? "Clearing…" : "Clear"}
                </button>
              </div>

              {issues.map((i) => (
                <div
                  key={i.id}
                  style={{
                    borderLeft: "3px solid var(--accent-red)",
                    paddingLeft: 10,
                    marginBottom: 12,
                  }}
                >
                  {/* THE COUNT IS SHOWN BECAUSE 1.831.0 STOPPED APPENDING A ROW
                      PER OCCURRENCE. Before it, a stuck purchase raised about
                      thirty identical issues in one polling session and at
                      around seven stuck purchases the list held nothing else.
                      Collapsing them fixed that - and without the count an
                      operator would read a problem that is still happening as
                      one that happened once, which is the opposite trade. */}
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {KIND_LABEL[i.kind] || "Issue"}
                    {i.count > 1 && ` · seen ${i.count} times`}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 2 }}>{i.message}</div>
                  {i.detail && (
                    <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 3, fontFamily: "monospace" }}>
                      {i.detail}
                    </div>
                  )}
                  {/* A LATER detail THAT DIFFERS IS ITS OWN FACT. A reconcile
                      that timed out twice and was then refused by the provider
                      is two things, and the count alone shows neither. */}
                  {i.lastDetail && (
                    <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 3, fontFamily: "monospace" }}>
                      most recently: {i.lastDetail}
                    </div>
                  )}
                  {/* createdAt IS FIRST-SEEN AND STAYS SO. lastSeenAt is absent
                      on rows written before 1.831.0, which is why it falls back
                      rather than rendering "Invalid Date". */}
                  <div style={{ fontSize: 11.5, opacity: 0.55, marginTop: 3 }}>
                    {i.count > 1
                      ? `first ${fmt(i.createdAt)} · last ${fmt(i.lastSeenAt || i.createdAt)}`
                      : fmt(i.createdAt)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
